// Helpers for the bulk media delete flow (POST /media_files/bulk_destroy and
// its bulk_destroy_status poll). Kept free of React/redux so they stay testable.

// Mirrors MediaFiles::BulkDestroyer::SYNC_LIMIT and MAX_BATCH on the backend.
export const BULK_DELETE_SYNC_LIMIT = 50;
export const BULK_DELETE_MAX_BATCH = 1000;

// Poll interval for a queued (202) batch.
export const BULK_DELETE_POLL_INTERVAL = 3000;

// Wording for failed[].reason. The backend treats this list as open, so
// anything unknown falls back to a generic line instead of showing the raw key.
const FAILURE_REASON_TEXT = {
  not_found: 'No longer available',
  forbidden: "You don't have permission to delete this",
  not_owner: 'Only the person who uploaded this can delete it',
  project_not_found: 'Project unavailable',
  file_missing: "Can't be deleted right now — try again later",
};

export const failureReasonText = (reason) => {
  if (!reason) return "Couldn't be deleted";
  return FAILURE_REASON_TEXT[reason] || "Couldn't be deleted";
};

// BMD-01 / BMD-04: failed[].name is null whenever the caller has no right to see
// the file - `forbidden` and `project_not_found` - and for `not_found`, where
// there is no record left to name. Only `not_owner`, `file_missing` and the
// deleted[] entries carry a real name.
//
// The user picked these files off the table, so the name is already in our own
// selection state: look it up there before falling back. The fallback is a
// generic label rather than `File #<id>` - the media id is a backend detail that
// tells the user nothing about which of their files was refused.
export const GENERIC_FILE_LABEL = 'This file';

// Snapshot of id -> name taken at submit time. The list is refetched once the
// delete lands, so a name read lazily off the table would already be gone for
// exactly the rows we still have to name.
export const mediaNamesById = (files) =>
  (Array.isArray(files) ? files : []).reduce((names, file) => {
    if (file?.id != null && file?.name) {
      names[file.id] = file.name;
    }
    return names;
  }, {});

// Returns the raw filename or null - never the string "null"/"undefined". The
// caller decides how to present it (the popup strips the extension).
export const resolveFailedFileName = (file, namesById) => {
  if (file?.name) return file.name;
  const known = file?.id == null ? null : namesById?.[file.id];
  return known || null;
};

// Normalises whatever the table hands us into unique, numeric, ascending ids.
export const normalizeMediaIds = (ids) => {
  if (!Array.isArray(ids)) return [];
  const numericIds = ids
    .map((id) => parseInt(id, 10))
    .filter((id) => !isNaN(id));
  return [...new Set(numericIds)].sort((a, b) => a - b);
};

// `ids` is sent as "[1,2,3]" — one of the shapes the endpoint accepts — because
// Request() flattens params through FormData, which would otherwise coerce an
// array to a bare comma string.
export const buildBulkDeletePayload = (ids) => ({
  ids: JSON.stringify(normalizeMediaIds(ids)),
});

// The envelope hardcodes http_code: 200 even on the async path, so the queued
// mode is detected from the HTTP status / the presence of bulk_deletion_id.
export const isQueuedResponse = (response) =>
  response?.status === 202 || !!response?.data?.data?.bulk_deletion_id;

export const bulkDeletionIdFrom = (response) =>
  response?.data?.data?.bulk_deletion_id ?? null;

// A 200 does not mean everything was deleted — the arrays are the source of truth.
export const bulkDeleteResultFrom = (payload) => ({
  deleted: Array.isArray(payload?.deleted) ? payload.deleted : [],
  failed: Array.isArray(payload?.failed) ? payload.failed : [],
});

export const deletedIdsFrom = (result) =>
  bulkDeleteResultFrom(result)
    .deleted.map((file) => file?.id)
    .filter((id) => id != null);

// file_removed: false is not a failure — the row went away, the physical file
// stayed because another media file still points at it. Never surfaced.
export const bulkDeleteSummaryMessage = (result) => {
  const { deleted, failed } = bulkDeleteResultFrom(result);
  const deletedLabel = `${deleted.length} ${deleted.length === 1 ? 'file' : 'files'}`;

  if (deleted.length === 0 && failed.length === 0) {
    return { http_code: 422, message: 'No files were deleted.' };
  }
  if (failed.length === 0) {
    return { http_code: 200, message: `${deletedLabel} deleted.` };
  }
  if (deleted.length === 0) {
    return {
      http_code: 422,
      message: `${failed.length} ${failed.length === 1 ? 'file' : 'files'} couldn't be deleted.`,
    };
  }
  return {
    http_code: 422,
    message: `${deletedLabel} deleted, ${failed.length} couldn't be deleted.`,
  };
};

// A batch where everything went through has nothing to read in the result view —
// the snackbar already reports it, so the popup can close itself. Anything with a
// failure stays open, since that list is the only place the reasons are shown.
export const isCleanBulkDeleteResult = (result) => {
  const { deleted, failed } = bulkDeleteResultFrom(result);
  return deleted.length > 0 && failed.length === 0;
};

// Client-side guard so the 1000 cap is never hit as a surprise 422.
export const bulkDeleteSelectionError = (ids) => {
  const normalized = normalizeMediaIds(ids);
  if (normalized.length === 0) return 'No media files selected!!';
  if (normalized.length > BULK_DELETE_MAX_BATCH) {
    return `Please select fewer than ${BULK_DELETE_MAX_BATCH} files.`;
  }
  return null;
};

export const isQueuedBatchFinished = (statusData) => statusData?.finished === true;

// status: "failed" means the job itself broke — not a completed batch that
// contains failures. The job does not retry, so this stays failed.
export const isQueuedBatchBroken = (statusData) =>
  statusData?.status === 'failed';
